import { useEffect, useMemo, useState } from 'react';
import { ExerciseDetail } from '../components/ExerciseDetail';
import { ExerciseFrames } from '../components/ExerciseImages';
import { IconCheck, IconInfo, IconMinus, IconPlates, IconPlus, IconSwap } from '../components/Icons';
import { RestTimer } from '../components/RestTimer';
import { Sheet } from '../components/Sheet';
import { SLOT_LABEL } from '../data/exercise-types';
import { getExercise } from '../data/exercises';
import { MUSCLES } from '../data/muscles';
import { alternativesFor } from '../engine/generator';
import { findLastPerformance } from '../engine/progression';
import { warmupPlan, warmupTargets } from '../engine/warmup';
import { minutesBetween } from '../lib/dates';
import { usesPlates } from '../lib/plates';
import { PlateCalculator } from '../components/PlateCalculator';
import { useApp } from '../store/AppStore';
import type { LoggedExercise } from '../store/types';

export function Workout({ onFinished }: { onFinished: () => void }) {
  const { state, finishWorkout, cancelWorkout } = useApp();
  const workout = state.activeWorkout;

  const [rest, setRest] = useState<{ startedAt: number; seconds: number } | null>(null);
  const [swapIndex, setSwapIndex] = useState<number | null>(null);
  const [detailIndex, setDetailIndex] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!workout) return;
    const tick = () => setElapsed(minutesBetween(workout.startedAt, new Date().toISOString()));
    tick();
    const id = window.setInterval(tick, 20_000);
    return () => window.clearInterval(id);
  }, [workout]);

  // Consecutive exercises sharing a superset group render inside one frame.
  const groups = useMemo(() => {
    const result: { groupId?: string; items: { logged: LoggedExercise; index: number }[] }[] = [];
    (workout?.exercises ?? []).forEach((logged, index) => {
      const last = result[result.length - 1];
      if (logged.supersetGroup && last?.groupId === logged.supersetGroup) {
        last.items.push({ logged, index });
      } else {
        result.push({ groupId: logged.supersetGroup, items: [{ logged, index }] });
      }
    });
    return result;
  }, [workout?.exercises]);

  // Which lifts today are worth a ramp — first compound of each pattern.
  const warmupIds = useMemo(
    () =>
      warmupTargets(
        (workout?.exercises ?? []).map((e) => ({
          exerciseId: e.exerciseId,
          exercise: getExercise(e.exerciseId),
        })),
      ),
    [workout?.exercises],
  );

  if (!workout) {
    return (
      <div className="empty">
        <p>No workout in progress.</p>
        <button className="btn" onClick={onFinished}>
          Back to today
        </button>
      </div>
    );
  }

  const completedSets = workout.exercises.reduce(
    (n, e) => n + e.sets.filter((s) => s.done).length,
    0,
  );
  const plannedSets = workout.exercises
    .filter((e) => !e.skipped)
    .reduce((n, e) => n + e.sets.length, 0);
  const progress = plannedSets === 0 ? 0 : Math.round((completedSets / plannedSets) * 100);

  return (
    <div className="stack">
      <div className="card">
        <div className="row between">
          <div>
            <h2>{workout.templateName}</h2>
            <div className="tiny muted">
              Block {workout.blockNumber} · week {workout.weekInBlock}
              {workout.isDeload ? ' · deload' : ''}
            </div>
          </div>
          <div className="center">
            <div className="mono" style={{ fontSize: 19, fontWeight: 700 }}>
              {elapsed}
            </div>
            <div className="tiny faint">min</div>
          </div>
        </div>
        <div className="bar" style={{ marginTop: 11 }}>
          <span style={{ width: `${progress}%`, background: 'var(--accent)' }} />
        </div>
        <div className="row between tiny faint" style={{ marginTop: 6 }}>
          <span>
            {completedSets} of {plannedSets} sets
          </span>
          <span>{progress}%</span>
        </div>
      </div>

      {groups.map((group, groupIndex) => {
        const blocks = group.items.map((item, i) => (
          <ExerciseBlock
            key={`${item.logged.exerciseId}-${item.index}`}
            logged={item.logged}
            index={item.index}
            showWarmup={warmupIds.has(item.logged.exerciseId)}
            supersetLetter={group.groupId ? (i === 0 ? 'A' : 'B') : undefined}
            // In a pair you only take a full rest after the second exercise;
            // between A and B you just walk over.
            restOverride={group.groupId && i === 0 ? 20 : undefined}
            onRest={(seconds) => setRest({ startedAt: Date.now(), seconds })}
            onSwap={() => setSwapIndex(item.index)}
            onInfo={() => setDetailIndex(item.index)}
          />
        ));

        if (!group.groupId) return blocks;

        return (
          <div className="superset" key={group.groupId ?? groupIndex}>
            <div className="superset-head">
              <span className="pill accent">Superset</span>
              <span className="tiny faint">
                Alternate A and B, full rest only after B
              </span>
            </div>
            {blocks}
          </div>
        );
      })}

      <div className="card">
        <div className="field">
          <label htmlFor="session-notes">Session notes</label>
          <textarea
            id="session-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Energy, sleep, niggles, anything worth remembering next time"
          />
        </div>
      </div>

      <button
        className="btn primary lg block"
        disabled={completedSets === 0}
        onClick={() => setConfirmFinish(true)}
      >
        {completedSets === 0 ? 'Log a set to finish' : 'Finish workout'}
      </button>
      <button
        className="btn ghost block"
        onClick={() => {
          if (confirm('Discard this workout? Nothing will be saved.')) {
            cancelWorkout();
            onFinished();
          }
        }}
      >
        Discard
      </button>

      {rest && (
        <RestTimer startedAt={rest.startedAt} seconds={rest.seconds} onDismiss={() => setRest(null)} />
      )}

      <SwapSheet index={swapIndex} onClose={() => setSwapIndex(null)} />
      <DetailSheet index={detailIndex} onClose={() => setDetailIndex(null)} />

      <Sheet open={confirmFinish} title="Finish workout" onClose={() => setConfirmFinish(false)}>
        <p className="muted small">
          {completedSets} sets logged across{' '}
          {workout.exercises.filter((e) => e.sets.some((s) => s.done)).length} exercises in {elapsed}{' '}
          minutes. Anything you did not tick off gets dropped, so your volume numbers stay honest.
        </p>
        <button
          className="btn primary lg block"
          style={{ marginTop: 14 }}
          onClick={() => {
            finishWorkout(notes.trim() || undefined);
            setConfirmFinish(false);
            onFinished();
          }}
        >
          Save it
        </button>
      </Sheet>
    </div>
  );
}

function ExerciseBlock({
  logged,
  index,
  showWarmup,
  supersetLetter,
  restOverride,
  onRest,
  onSwap,
  onInfo,
}: {
  logged: LoggedExercise;
  index: number;
  showWarmup: boolean;
  supersetLetter?: 'A' | 'B';
  restOverride?: number;
  onRest: (seconds: number) => void;
  onSwap: () => void;
  onInfo: () => void;
}) {
  const { state, updateSet, addSet, removeSet, skipExercise, toggleWarmup } = useApp();
  const [showImages, setShowImages] = useState(false);
  const [plateTarget, setPlateTarget] = useState<number | null>(null);
  const exercise = getExercise(logged.exerciseId);

  const last = useMemo(
    () => findLastPerformance(state.workouts, logged.exerciseId),
    [state.workouts, logged.exerciseId],
  );

  // Whatever you're actually going to lift today: what you've typed into the
  // first set if you've typed anything, otherwise the suggestion.
  const workingWeight = logged.sets[0]?.weightKg || logged.suggestionKg;

  const warmups = useMemo(
    () => (showWarmup && exercise ? warmupPlan(exercise, workingWeight) : []),
    [showWarmup, exercise, workingWeight],
  );

  if (!exercise) return null;

  const allDone = logged.sets.every((s) => s.done);
  const showPlates = usesPlates(exercise.loadType);

  return (
    <div className={`exercise${allDone ? ' done' : ''}${logged.skipped ? ' skipped' : ''}`}>
      <div className="exercise-head">
        <div className="grow">
          <div className="tiny faint" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {SLOT_LABEL[logged.slot]}
          </div>
          <h3 style={{ margin: '2px 0 4px' }} className="row">
            {supersetLetter && <span className="superset-letter">{supersetLetter}</span>}
            {exercise.name}
          </h3>
          <div className="row wrap" style={{ gap: 5 }}>
            <span className="pill accent">
              {logged.targetSets} × {logged.targetReps[0]}–{logged.targetReps[1]}
            </span>
            {exercise.primary.slice(0, 2).map((m) => (
              <span className="pill" key={m}>
                {MUSCLES[m].short}
              </span>
            ))}
          </div>
        </div>
        <div className="stack" style={{ gap: 5 }}>
          <button className="btn ghost sm" onClick={onInfo} aria-label="Exercise details">
            <IconInfo size={15} />
          </button>
          <button className="btn ghost sm" onClick={onSwap} aria-label="Swap exercise">
            <IconSwap size={15} />
          </button>
          {showPlates && (
            <button
              className="btn ghost sm"
              onClick={() => setPlateTarget(workingWeight ?? 0)}
              aria-label="Plate calculator"
              disabled={!workingWeight}
            >
              <IconPlates size={15} />
            </button>
          )}
        </div>
      </div>

      {logged.suggestionReason && !logged.skipped && (
        <div className="note small" style={{ margin: '0 12px 10px' }}>
          {logged.suggestionReason}
          {last && (
            <div className="tiny faint" style={{ marginTop: 3 }}>
              Last time:{' '}
              {last.logged.sets
                .filter((s) => s.done)
                .map((s) => `${s.weightKg}×${s.reps}`)
                .join(', ')}
            </div>
          )}
        </div>
      )}

      {state.settings.showImages && (
        <div style={{ padding: '0 12px 10px' }}>
          {showImages ? (
            <ExerciseFrames exerciseId={exercise.id} />
          ) : (
            <button className="btn ghost sm block" onClick={() => setShowImages(true)}>
              Show me how
            </button>
          )}
        </div>
      )}

      {!logged.skipped && warmups.length > 0 && (
        <div className="warmups">
          <div className="row between" style={{ marginBottom: 8 }}>
            <span className="card-title" style={{ margin: 0 }}>
              Warm-up
            </span>
            <span className="tiny faint">not counted in your volume</span>
          </div>

          {warmups.map((set, i) => {
            const done = logged.warmupsDone?.[i] ?? false;
            return (
              <div className={`warmup-row${done ? ' done' : ''}`} key={`${set.label}-${i}`}>
                <span className="tag">{set.label}</span>
                {showPlates ? (
                  <button
                    className="load"
                    onClick={() => setPlateTarget(set.weightKg)}
                    aria-label={`Show plates for ${set.weightKg} kg`}
                  >
                    {set.weightKg} kg
                  </button>
                ) : (
                  <span className="load" style={{ textDecoration: 'none' }}>
                    {set.weightKg} kg
                  </span>
                )}
                <span className="reps">× {set.reps}</span>
                <button
                  className={`check small${done ? ' on' : ''}`}
                  aria-label={done ? 'Mark warm-up incomplete' : 'Mark warm-up complete'}
                  onClick={() => toggleWarmup(index, i)}
                >
                  <IconCheck size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!logged.skipped && (
        <div className="sets">
          <div className="set-header">
            <span>#</span>
            <span>kg</span>
            <span>reps</span>
            <span>RPE</span>
            <span />
          </div>

          {logged.sets.map((set, setIndex) => (
            <div className={`set-row${set.done ? ' done' : ''}`} key={setIndex}>
              <span className="idx">{setIndex + 1}</span>
              <input
                type="number"
                inputMode="decimal"
                step="0.5"
                value={set.weightKg || ''}
                placeholder={String(logged.suggestionKg ?? 0)}
                onChange={(e) =>
                  updateSet(index, setIndex, { weightKg: Number(e.target.value) || 0 })
                }
                aria-label={`Set ${setIndex + 1} weight in kg`}
              />
              <input
                type="number"
                inputMode="numeric"
                value={set.reps || ''}
                placeholder={String(logged.targetReps[1])}
                onChange={(e) => updateSet(index, setIndex, { reps: Number(e.target.value) || 0 })}
                aria-label={`Set ${setIndex + 1} reps`}
              />
              <input
                type="number"
                inputMode="decimal"
                step="0.5"
                min="5"
                max="10"
                value={set.rpe ?? ''}
                placeholder="–"
                onChange={(e) =>
                  updateSet(index, setIndex, {
                    rpe: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
                aria-label={`Set ${setIndex + 1} RPE`}
              />
              <button
                className={`check${set.done ? ' on' : ''}`}
                aria-label={set.done ? 'Mark set incomplete' : 'Mark set complete'}
                onClick={() => {
                  const nextDone = !set.done;
                  updateSet(index, setIndex, { done: nextDone });
                  // Starting the rest clock on the tick is the whole ergonomic
                  // win — you never have to reach for a separate timer.
                  if (nextDone) onRest(restOverride ?? exercise.restSeconds);
                }}
              >
                <IconCheck size={16} />
              </button>
            </div>
          ))}

          <div className="row" style={{ gap: 7, marginTop: 9 }}>
            <button className="btn ghost sm" onClick={() => addSet(index)}>
              <IconPlus size={14} /> Set
            </button>
            <button
              className="btn ghost sm"
              onClick={() => removeSet(index)}
              disabled={logged.sets.length <= 1}
            >
              <IconMinus size={14} />
            </button>
            <div className="grow" />
            <button className="btn ghost sm" onClick={() => skipExercise(index)}>
              Skip
            </button>
          </div>
        </div>
      )}

      {logged.skipped && (
        <div className="row between" style={{ padding: '0 12px 12px' }}>
          <span className="tiny faint">Skipped</span>
          <button className="btn ghost sm" onClick={() => skipExercise(index)}>
            Put it back
          </button>
        </div>
      )}

      <PlateCalculator
        open={plateTarget !== null}
        targetKg={plateTarget ?? 0}
        onClose={() => setPlateTarget(null)}
      />
    </div>
  );
}

function SwapSheet({ index, onClose }: { index: number | null; onClose: () => void }) {
  const { state, swapExercise } = useApp();
  const workout = state.activeWorkout;
  const logged = index === null ? undefined : workout?.exercises[index];

  const options = useMemo(() => {
    if (!logged || !workout) return [];
    return alternativesFor(
      logged.exerciseId,
      logged.slot,
      new Set(state.settings.equipment),
      workout.exercises.map((e) => e.exerciseId),
    );
  }, [logged, workout, state.settings.equipment]);

  if (index === null || !logged) return null;

  return (
    <Sheet open title="Swap exercise" onClose={onClose}>
      <p className="small muted">
        Machine taken, or just fancy something else? These all train the same slot
        with the equipment you have.
      </p>
      <div className="card flush" style={{ marginTop: 12 }}>
        {options.length === 0 && (
          <div className="empty small">No alternatives available with your current equipment.</div>
        )}
        {options.map((ex) => (
          <button
            className="list-item"
            key={ex.id}
            onClick={() => {
              swapExercise(index, ex.id);
              onClose();
            }}
          >
            <div className="grow">
              <div style={{ fontWeight: 600 }}>{ex.name}</div>
              <div className="tiny faint">
                {ex.primary.map((m) => MUSCLES[m].short).join(', ')} · {ex.defaultSets} ×{' '}
                {ex.repRange[0]}–{ex.repRange[1]}
              </div>
            </div>
            <IconSwap size={15} />
          </button>
        ))}
      </div>
    </Sheet>
  );
}

function DetailSheet({ index, onClose }: { index: number | null; onClose: () => void }) {
  const { state, swapExercise } = useApp();
  const logged = index === null ? undefined : state.activeWorkout?.exercises[index];
  const exercise = logged ? getExercise(logged.exerciseId) : undefined;

  if (index === null || !exercise) return null;

  return (
    <Sheet open title={exercise.name} onClose={onClose}>
      <ExerciseDetail
        exercise={exercise}
        onPick={(id) => {
          swapExercise(index, id);
          onClose();
        }}
      />
    </Sheet>
  );
}
